import { Icon, Stack } from '@fluentui/react';
import { Button, buttonStylesIcon, buttonStylesIconCard, IBasicColumn, IconName } from '@h2oai/ui-kit';

import type { S3Object } from '../../../drive/types';
import type { FileProgressEntry } from '../../../drive/useFileProgress';
import { formatDefaultDateAndTime } from '../../../utils/utils';
import { SortableHeader } from '../components/SortableHeader';
import type { SortableField, SortDirection } from '../types/sorting';
import { ensureTrailingSlash, getLastPathSegment, getParentPath, isDirectory } from './pathUtils';

const getTargetPath = (item: S3Object, currentPath?: string): string => {
  if (item.isParentDirectory) {
    if (!currentPath) return '';
    return getParentPath(currentPath);
  }
  return item.Key || '';
};

const getIconName = (item: S3Object): string => {
  if (item.isParentDirectory) return 'ChevronLeftMed';
  return item.Key && isDirectory(item.Key) ? 'FolderHorizontal' : 'FileCode';
};

const getBaseName = (item: S3Object, currentPath?: string): string => {
  const key = item.Key || 'Unknown';

  if (item.isParentDirectory) return '../';

  if (currentPath && key.startsWith(currentPath)) {
    const normalizedCurrentPath = ensureTrailingSlash(currentPath);
    return key.substring(normalizedCurrentPath.length);
  }

  const lastName = getLastPathSegment(key);
  return isDirectory(key) ? `${lastName}/` : lastName;
};

const getProgressText = (progressEntry?: FileProgressEntry): string => {
  if (!progressEntry) return '';

  if (progressEntry.isCompleted) {
    const action = progressEntry.type === 'upload' ? 'Uploaded' : 'Downloaded';
    return `${action}!`;
  }

  if (progressEntry.progress === 0) {
    const action = progressEntry.type === 'upload' ? 'Initializing upload' : 'Initializing download';
    return `${action}...`;
  }

  const action = progressEntry.type === 'upload' ? 'Uploading' : 'Downloading';
  return `${action}... ${progressEntry.progress}%`;
};

export const createFileTableColumns = (
  onDeleteFile: (key: string) => void,
  isWriter: boolean,
  currentPath?: string,
  onDirectoryClick?: (directoryPath: string) => void,
  onCopyFile?: (key: string) => void,
  onMoveFile?: (key: string) => void,
  onRenameFile?: (item: S3Object) => void,
  onDownloadFile?: (key: string) => void,
  sortField?: SortableField | null,
  sortDirection?: SortDirection,
  onSort?: (field: SortableField) => void,
  onCopyLinkItem?: (item: S3Object) => void,
  onShareAccessItem?: (item: S3Object) => void,
  fileProgressMap?: Map<string, FileProgressEntry>,
  canDownload?: boolean
): IBasicColumn[] => [
  {
    key: 'Key',
    name: 'File Name',
    fieldName: 'Key',
    minWidth: 200,
    flexGrow: 1,
    onRenderHeader: (column) => (
      <SortableHeader
        columnName={column.name}
        field="Key"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
      />
    ),
    data: {
      listCellProps: {
        onRenderText: (item: S3Object) => {
          const isDir = (item.Key && isDirectory(item.Key)) || item.isParentDirectory;
          const baseName = getBaseName(item, currentPath);
          const progressEntry = fileProgressMap?.get(item.Key || '');
          const progressText = getProgressText(progressEntry);

          const content = (
            <Stack
              horizontal
              horizontalAlign="space-between"
              verticalAlign="center"
              styles={{ root: { width: '100%' } }}
            >
              <Stack
                horizontal
                tokens={{ childrenGap: 8 }}
                verticalAlign="center"
                styles={{ root: { minWidth: 0, flexShrink: 1 } }}
              >
                <Icon iconName={getIconName(item)} style={{ fontSize: '16px', flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{baseName}</span>
              </Stack>
              {progressText && (
                <span
                  style={{
                    fontStyle: 'italic',
                    color: 'var(--h2o-gray600)',
                    paddingLeft: 8,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {progressText}
                </span>
              )}
            </Stack>
          );

          if (isDir && onDirectoryClick) {
            const targetPath = getTargetPath(item, currentPath);

            return (
              <div onClick={() => onDirectoryClick(targetPath)} style={{ cursor: 'pointer' }}>
                {content}
              </div>
            );
          }

          return content;
        },
      },
    },
  },
  {
    key: 'Size',
    name: 'Size',
    fieldName: 'Size',
    minWidth: 100,
    onRenderHeader: (column) => (
      <SortableHeader
        columnName={column.name}
        field="Size"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
      />
    ),
    data: {
      listCellProps: {
        onRenderText: (item: S3Object) => {
          const isItemDirectory = item.Key ? isDirectory(item.Key) : false;
          if (isItemDirectory) return '-';

          const size = item.Size;
          if (!size) return 'Unknown';
          if (size < 1024) return `${size} B`;
          if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
          if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
          return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
        },
      },
    },
  },
  {
    key: 'Type',
    name: 'Type',
    fieldName: 'Type',
    minWidth: 120,
    onRenderHeader: (column) => (
      <SortableHeader
        columnName={column.name}
        field="Type"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
      />
    ),
    data: {
      listCellProps: {
        onRenderText: (item: S3Object) => {
          const isDir = item.Key?.endsWith('/') || item.isParentDirectory;
          if (isDir) return 'directory';

          const key = item.Key || '';
          const lastDotIndex = key.lastIndexOf('.');
          const ext = lastDotIndex > key.lastIndexOf('/') ? key.substring(lastDotIndex + 1) : '';
          return ext || 'unknown';
        },
      },
    },
  },
  {
    key: 'LastModified',
    name: 'Last Modified',
    fieldName: 'LastModified',
    minWidth: 250,
    onRenderHeader: (column) => (
      <SortableHeader
        columnName={column.name}
        field="LastModified"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
      />
    ),
    data: {
      listCellProps: {
        onRenderText: (item: S3Object) => formatDefaultDateAndTime(item.LastModified) || 'Unknown',
      },
    },
  },
  {
    key: 'drive-item-actions',
    name: 'Action',
    minWidth: 120,
    data: {
      listCellProps: {
        onRenderText: (item: S3Object) => {
          const menuItems = [];
          const progressEntry = fileProgressMap?.get(item.Key || '');

          if (isWriter && item.Key && !item.isParentDirectory) {
            menuItems.push({
              key: 'drive-item-rename',
              dataTest: 'drive-item-rename',
              text: 'Rename',
              onClick: () => {
                if (item.Key) onRenameFile?.(item);
              },
              iconProps: { iconName: 'Edit' },
            });
          }

          if (isWriter && item.Key && !item.isParentDirectory && !isDirectory(item.Key)) {
            menuItems.push(
              {
                key: 'drive-item-copy',
                dataTest: 'drive-item-copy',
                text: 'Copy',
                onClick: () => {
                  if (item.Key) onCopyFile?.(item.Key);
                },
                iconProps: { iconName: 'Copy' },
              },
              {
                key: 'drive-item-move',
                dataTest: 'drive-item-move',
                text: 'Move',
                onClick: () => {
                  if (item.Key) onMoveFile?.(item.Key);
                },
                iconProps: { iconName: 'MoveToFolder' },
              }
            );
          }

          if (item.Key && !item.isParentDirectory && !isDirectory(item.Key) && canDownload) {
            const isDownloading = progressEntry?.type === 'download' && !progressEntry.isCompleted;
            menuItems.push({
              key: 'drive-item-download',
              dataTest: 'drive-item-download',
              text: 'Download',
              onClick: () => {
                onDownloadFile?.(item.Key!);
              },
              iconProps: { iconName: 'DownloadDocument' },
              disabled: isDownloading,
            });
          }

          if (item.Key && !item.isParentDirectory && !isDirectory(item.Key)) {
            menuItems.push({
              key: 'drive-item-get-link',
              dataTest: 'drive-item-get-link',
              text: 'Get link',
              onClick: () => onCopyLinkItem?.(item),
              iconProps: { iconName: 'Link' },
            });
          }

          if (item.Key && !item.isParentDirectory) {
            menuItems.push({
              key: 'drive-item-share-access',
              dataTest: 'drive-item-share-access',
              text: 'Share',
              onClick: () => onShareAccessItem?.(item),
              iconProps: { iconName: 'Share' },
            });
          }

          if (isWriter && item.Key && !item.isParentDirectory) {
            menuItems.push({
              key: 'drive-item-delete',
              dataTest: 'drive-item-delete',
              text: 'Delete',
              onClick: () => {
                if (item.Key) onDeleteFile(item.Key);
              },
              style: { color: 'var(--h2o-red400)' },
              iconProps: {
                iconName: 'Delete',
                style: { color: 'var(--h2o-red400)' },
              },
            });
          }

          return menuItems.length > 0 ? (
            <Button
              styles={[
                buttonStylesIcon,
                buttonStylesIconCard,
                { root: { minWidth: 32, i: { transform: 'rotate(90deg)' } } },
              ]}
              menuIconName={IconName.More}
              menuItems={menuItems}
            />
          ) : null;
        },
      },
    },
  },
];
